import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import "./Help.css";

const Help = () => {
  const helpItems = [
    {
      title: "Dashboard",
      image: "/dev.nmm.gfg/assets/images/help/dashboard.png",
      description:
        "Your home screen. Shows a summary of your membership status, recent activity, and quick links to the most-used sections. Use it as your starting point every time you log in.",
    },
    {
      title: "Profile",
      image: "/dev.nmm.gfg/assets/images/help/profile.png",
      description:
        "View and update your personal and company details — name, contact number, address, logo, banner, bio, social media links, and branch offices. Keep this up to date so other members can find and connect with you.",
    },
    {
      title: "Membership Plan",
      image: "/dev.nmm.gfg/assets/images/help/membership.png",
      description:
        "See your current membership plan, its expiry date, and the features it includes. If you want to move to a higher plan, click Upgrade and submit a request — the admin will review and approve it.",
    },
    {
      title: "Events",
      image: "/dev.nmm.gfg/assets/images/help/events.png",
      description:
        "Browse all network events. Click on any event card to see full details — date, location, description, and registration fee. For upcoming events, click Register to submit your attendance request.",
    },
    {
      title: "News",
      image: "/dev.nmm.gfg/assets/images/help/news.png",
      description:
        "Stay up to date with the latest news and announcements from the network. Click Read More on any article to read the full story.",
    },
    {
      title: "Community (Timeline)",
      image: "/dev.nmm.gfg/assets/images/help/timeline.png",
      description:
        "The community feed where members share updates, photos, and videos. You can post your own content, like and comment on others' posts, and stay connected with the network.",
    },
    {
      title: "Chat",
      image: "/dev.nmm.gfg/assets/images/help/chat.png",
      description:
        "Send direct messages to any member. Search by company name or member name in the sidebar, open a conversation, and start chatting in real time.",
    },
    {
      title: "Member Directory",
      image: "/dev.nmm.gfg/assets/images/help/directory.png",
      description:
        "A searchable list of all active members in the network. Click on any member to view their profile, contact details, and company information.",
    },
    {
      title: "Active Members",
      image: "/dev.nmm.gfg/assets/images/help/active-members.png",
      description:
        "Shows only members with an active membership status. Use this to find verified, currently active companies in the network.",
    },
    {
      title: "Recent Members",
      image: "/dev.nmm.gfg/assets/images/help/recent-members.png",
      description:
        "Displays the newest members who have recently joined the network. A good way to discover and connect with new companies.",
    },
    {
      title: "Members by Country",
      image: "/dev.nmm.gfg/assets/images/help/members-by-country.png",
      description:
        "Filter and browse members by their country. Useful when you are looking for partners or contacts in a specific region.",
    },
    {
      title: "Business Impact",
      image: "/dev.nmm.gfg/assets/images/help/business-impact.png",
      description:
        "Track your business activity within the network — transactions, wallet balance, and impact metrics. Use Add Business to log new business activity and Wallet to see your balance.",
    },
    {
      title: "Freight Requests",
      image: "/dev.nmm.gfg/assets/images/help/freight-requests.png",
      description:
        "Manage freight collaboration requests with other members. New Request lets you send a request, Sent shows what you have sent, Received shows incoming requests, and Accepted shows confirmed collaborations.",
    },
    {
      title: "Withdrawal",
      image: "/dev.nmm.gfg/assets/images/help/withdrawal.png",
      description:
        "Submit a request to withdraw funds from your business wallet. Fill in the amount and bank details, and the admin will process your request.",
    },
    {
      title: "Invoices",
      image: "/dev.nmm.gfg/assets/images/help/invoices.png",
      description:
        "View all invoices issued to you — membership invoices, event registration invoices, and any custom invoices from the admin. You can download any invoice as a PDF.",
    },
    {
      title: "Gallery",
      image: "/dev.nmm.gfg/assets/images/help/gallery.png",
      description:
        "Browse and upload photos, albums, and videos. Organise your media into albums and share them with the network community.",
    },
    {
      title: "Certificates",
      image: "/dev.nmm.gfg/assets/images/help/certificates.png",
      description:
        "View and download certificates associated with your membership or event participation.",
    },
  ];

  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return helpItems;
    return helpItems.filter((i) => {
      return (
        i.title.toLowerCase().includes(term) ||
        (i.description || "").toLowerCase().includes(term)
      );
    });
  }, [q]);

  return (
    <motion.div
      className="hp-wrap"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 0.68, 0, 1.2] }}
    >
      <div className="hp-hero">
        <div>
          <div className="hp-kicker">
            <i className="bx bx-info-circle"></i>
            Help Center
          </div>
          <h1 className="hp-title">How can we help?</h1>
          <p className="hp-sub">
            Quick guides for using the Member Panel. Search a topic and open the
            step-by-step screenshots.
          </p>
          <div className="hp-search">
            <i className="bx bx-search"></i>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search: events, timeline, chat, invoices..."
              aria-label="Search help"
            />
            <span className="hp-pill">{filtered.length} topics</span>
          </div>
        </div>
        <div className="hp-hero-card">
          <div className="hp-hero-icon">
            <i className="bx bx-support"></i>
          </div>
          <div className="hp-hero-body">
            <div className="hp-hero-title">Need support?</div>
            <div className="hp-hero-text">
              If you’re stuck, check the guide or contact support from the
              Community/Chat modules.
            </div>
            <div className="hp-hero-actions">
              <Link to="/Chat" className="hp-btn" >
                <i className="bx bx-conversation"></i> Open Chat
              </Link>
              <Link to="/Timeline" className="hp-btn hp-btn--soft" >
                <i className="bx bx-news"></i> Go to Community
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="hp-grid">
        {filtered.length === 0 ? (
          <div className="hp-empty">
            <i className="bx bx-search-alt"></i>
            <h3>No matching topics</h3>
            <p>Try a different keyword like “events” or “membership”.</p>
            <button className="hp-btn" type="button" onClick={() => setQ("")}>
              Clear search
            </button>
          </div>
        ) : (
          filtered.map((item) => (
            <motion.article
              key={item.title}
              className="hp-card"
              whileHover={{ y: -4 }}
              transition={{ duration: 0.18 }}
            >
              <div className="hp-thumb">
                <img src={item.image} alt={item.title} loading="lazy" />
              </div>
              <div className="hp-body">
                <div className="hp-card-title">{item.title}</div>
                <div className="hp-card-desc">{item.description}</div>
              </div>
            </motion.article>
          ))
        )}
      </div>
    </motion.div>
  );
};

export default Help;


// const Help = () => {
//   const helpItems = [
//     {
//       title: "Home Page",
//       image: "/nmmSetups/demo_nmm/assets/images/help/dashboard.png",
//       description:
//         "This home page provides a snapshot of the current state of user registrations, geographical distribution, and membership status. It includes a chart showing the number of users registered over time, a map displaying user locations, and a summary of active and inactive members.",
//     },
//     {
//       title: "Events",
//       image: "/nmmSetups/demo_nmm/assets/images/help/events.png",
//       description:
//         "This page displays upcoming and past event information. You can click on any event to view all its details, including date, time, location, and description.",
//     },
//     {
//       title: "News",
//       image: "/nmmSetups/demo_nmm/assets/images/help/news.png",
//       description:
//         'The News page displays news insights, and clicking on "Read More" shows all the news.',
//     },
//     {
//       title: "Membership Plan",
//       image: "/nmmSetups/demo_nmm/assets/images/help/membershipplan.png",
//       description:
//         "Here, you can view your current membership plan and request an upgrade to access more features and benefits.",
//     },
//     {
//       title: "View Impact",
//       image: "/nmmSetups/demo_nmm/assets/images/help/viewImpact.png",
//       description:
//         "This page allows you to view the impact of your membership, including statistics and achievements.",
//     },
//     {
//       title: "Make Transaction",                                                                                                     
//       image: "/nmmSetups/demo_nmm/assets/images/help/makeTransaction.png",
//       description:
//         "You can make transactions related to your membership, such as payments or donations, from this page.",
//     },
//     {
//       title: "withdraw Request",
//       image: "/nmmSetups/demo_nmm/assets/images/help/withdrawForm.png",
//       description:
//         "This page allows you to withdraw funds or request a payout related to your membership.",
//     },
//     {
//       title: "New Request",
//       image: "/nmmSetups/demo_nmm/assets/images/help/newRequest.png",
//       description:
//         "You can submit new requests or inquiries related to your membership or services from this page.",
//     },
//     {
//       title: "User Profile",
//       image: "/nmmSetups/demo_nmm/assets/images/help/userProfile.png",
//       description:
//         "This page displays your user profile information, including personal details, contact information, and membership status.",
//     },
//     {
//       title: "User List",
//       image: "/nmmSetups/demo_nmm/assets/images/help/userList.png",
//       description:
//         "You can view a list of all users, including their profiles and membership details, from this page.",
//     },
//     {
//       title: "Recent Members",
//       image: "/nmmSetups/demo_nmm/assets/images/help/recentMembers.png",
//       description:
//         "This section displays the most recent members who have joined, along with their basic information.",
//     },
//     {
//       title: "Timeline",
//       image: "/nmmSetups/demo_nmm/assets/images/help/timeline.png",
//       description:
//         "You can upload photos, videos, and music from the timeline page. It also displays feeds and posts.",
//     },
//     {
//       title: "Chat",
//       image: "/nmmSetups/demo_nmm/assets/images/help/Chat.png",
//       description:
//         "You can search for members using their company name or owner name, and start chatting with them directly from the sidebar.",
//     },
//   ];

//   return (
//     <section className="content">
//       <div className="container-fluid">
//         <div className="row">
//           <div className="col-md-12">
//             <div className="card border-top border-0 border-4 border-primary">
//               <div className="card-body p-5">
//                 <div className="card-title d-flex align-items-center">
//                   <h5 className="mb-0 text-primary">Help Information</h5>
//                 </div>
//                 <hr />
//                 <div className="row">
//                   {helpItems.map((item, index) => (
//                     <div className="col-md-6 mb-4" key={index}>
//                       <div className="card shadow-sm h-100">
//                         <div
//                           className="card-header text-white text-center"
//                           style={{ backgroundColor: "#212529" }}
//                         >
//                           <h5 className="mb-0 text-white">{item.title}</h5>
//                         </div>
//                         <div className="card-body text-center">
//                           <img
//                             src={item.image}
//                             className="img-fluid mb-3"
//                             style={{ maxHeight: "200px" }}
//                             alt={item.title}
//                           />
//                           <p className="text-justify">{item.description}</p>
//                         </div>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default Help;